import { IVeriMapChildComponent, IVeriMapComponent } from "@directives/_map/veriMap/veriMapComponent/veriMap.component";
import { IGoogleMapsMarker, IGoogleMapsMarkerOptions } from "@interfaces/IGoogleMapsMarkerOptions";
import { IPlot, IPlotEvent } from "@interfaces/IPlot";
import { GoogleMarkerService } from "@services/googleMarker.service";
import { MapService } from "@services/map.service";
import { PlotService } from "@services/plot.service";
import { VeriMarkerService } from "@services/veriMarker.service";
import { IMarkerEvents, IMarkerOptions, IPolyline } from "@typings/google.maps.angular";
import { IRouteStop } from "src/app/main/components/planning/interfaces/IRouteStop";
import { ITmsReplayResult } from "src/app/main/components/planning/interfaces/ITmsReplayResult";
import { IPlannedRouteAudit } from "../../../../main/components/planning/interfaces/IPlannedRouteAudit";

interface IComponentBindings {
    routeStops: IRouteStop[];
    routeReplay: ITmsReplayResult;
    plots: IPlot[];
    fit: boolean;

    selectedAudit: IPlannedRouteAudit;

    veriMap: IVeriMapComponent;

    onAuditSelected(args: { audit: IPlannedRouteAudit, plot: IPlot, $event: MouseEvent });
}

class ComponentController implements angular.IComponentController, IComponentBindings, IVeriMapChildComponent {
    routeStops: IRouteStop[];
    routeReplay: ITmsReplayResult;
    plots: IPlot[];
    fit: boolean;

    highlightedPlot: IPlot;
    selectedAudit: IPlannedRouteAudit;

    veriMap: IVeriMapComponent;

    onAuditSelected: (args: { audit: IPlannedRouteAudit, plot: IPlot, $event: MouseEvent }) => void;

    mapPlots: IPlot[];
    polylines: IPolyline[] = [];

    latLngsToFitTo: google.maps.LatLng[] = [];

    eventMarkerOptions: IMarkerOptions<IPlotEvent> = {
        events: {},
        control: {},
        type: "cluster",
        typeOptions: {
            imagePath: "https://rawgit.com/googlemaps/js-marker-clusterer/gh-pages/images/m",
            maxZoom: 13,
            gridSize: 40 // default 60
        },
        typeEvents: {}
    };

    plotMarkerEvents: IMarkerEvents<IPlot> = {
        click: (markerObj, eventName: string, plot: IPlot, mouseEvent: MouseEvent) => {
            var matchingAudit = this.routeReplay.Audits.find(a => a.Update.PlotId === plot.Id)[0];

            if (this.onAuditSelected) {
                this.onAuditSelected({ audit: matchingAudit, plot: plot, $event: mouseEvent });
            }
        },
        mouseover: (markerObj, eventName: string, plot: IPlot, mouseEvent: MouseEvent) => {
            this.setHighlightedPlot(plot);
        }
    }

    polylineEvents = {
        mouseover: (p: google.maps.Polyline, eventName: string, model: any, args: any[]) => {
            var mouseEvent: google.maps.MouseEvent = args[0];
            this.polylineMouseOverEvent(mouseEvent.latLng);
        }
    };

    static $inject = ["tmsService", "plotService", "mapService", "veriMarkerService", "googleMarkerService"];
    constructor(
        private readonly plotService: PlotService,
        private readonly mapService: MapService,
        private readonly veriMarkerService: VeriMarkerService,
        private readonly googleMarkerService: GoogleMarkerService) {
    }

    $onInit(): void {
        this.veriMap.mapInitialisedPromise
            .then(map => {
                this.fitMap();
            })
            .catch(e => {
                console.error("onInit error", e);
            });

        this.veriMap.registerChildComponent(this);
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {

        if (onChangesObj["plots"] || onChangesObj["audits"]) {

            this.createPolylines();
            this.hidePlotsBasedOnZoomLevel();

            this.fitMap();
        }

        if (onChangesObj["routeStops"]) {
            this.updateMarkerIcons();
        }

        if (onChangesObj["selectedAudit"] && this.selectedAudit) {
            const matchingPlot = this.plots.find(p => p.Id === this.selectedAudit.PlotId);

            if (matchingPlot) {
                this.setHighlightedPlot(matchingPlot);
            }
        }
    }

    getLatLngsForFitMap(): google.maps.LatLng[] {

        return this.mapService.convertPlotsToGoogleLatLng(this.plots);
    }


    onMapIdle(): void {
        this.hidePlotsBasedOnZoomLevel();
    }

    stopsMarkers: IGoogleMapsMarker<IRouteStop>[] = [];

    private updateMarkerIcons() {
        this.stopsMarkers = [];

        if (!this.routeStops) {
            return;
        }

        this.routeStops.forEach((routeStop, index, visibleWaypoints) => {

            const markerOptions = this.createRouteMarkerOptions(routeStop, index, visibleWaypoints.length);

            markerOptions.draggable = false;

            markerOptions.position = this.getRouteStopCoords(routeStop);

            this.stopsMarkers.push({
                id: index,
                options: markerOptions,
                model: routeStop
            });
        });
    }

    private getRouteStopCoords(stop: IRouteStop): google.maps.LatLngLiteral {
        if (stop.GeocodedAddress) {
            return {
                lat: stop.GeocodedAddress.CenterPoint.Latitude,
                lng: stop.GeocodedAddress.CenterPoint.Longitude
            }
        }
        else if (stop.Geofence) {
            return {
                lat: stop.Geofence.Latitude,
                lng: stop.Geofence.Longitude
            }
        }
    }

    private hidePlotsBasedOnZoomLevel() {
        if (!this.plots) {
            this.mapPlots = [];
            return;
        }

        var plotsWithAudit: IPlot[] = [];

        if (this.routeReplay.Audits) {
            this.routeReplay.Audits.forEach(audit => {
                plotsWithAudit.push(this.plots.find(p => p.Id === audit.PlotId));
            });
        } else {
            plotsWithAudit = this.plots;
        }

        this.plotService.getVisisblePlotsBasedOnZoomLevel(plotsWithAudit, this.veriMap.getMap()).then(displayPlots => {
            this.mapPlots = displayPlots;
        });
    }


    private fitMap() {
        const latLngs = this.getLatLngsForFitMap();
        this.veriMap.fitMap(latLngs);
    }

    private polylineMouseOverEvent(latLng: google.maps.LatLng) {

        const closestPlot = this.plotService.findPlotClosestToLatLng(this.plots, latLng);

        this.setHighlightedPlot(closestPlot);
        this.highlightPloyline();
    }

    private getPolylineId(): number {
        return Math.floor(Math.random() * 100) + 1;
    }

    private createPolylines() {
        this.polylines = [];

        this.polylines.push(this.mapService.createPolyline(this.getPolylineId(), this.mapService.convertPlotsToGoogleLatLng(this.plots), "#27ae60", this.polylineEvents));
    }

    private highlightPloyline() {
        if (!this.highlightedPlot) {
            return;
        }

        this.polylines = [];
        const primaryPlots: IPlot[] = [];
        const secondaryPlots: IPlot[] = [];

        for (let i = 0; i < this.plots.length; i++) {
            const plot = this.plots[i];

            if (this.highlightedPlot.Id < plot.Id) {
                primaryPlots.push(plot);
            }
            else if (this.highlightedPlot.Id === plot.Id) {
                primaryPlots.push(plot);
                secondaryPlots.push(plot);
            }
            else {
                secondaryPlots.push(plot);
            }
        }

        if (primaryPlots.length > 0) {
            this.polylines.push(this.mapService.createPolyline(this.getPolylineId(), this.mapService.convertPlotsToGoogleLatLng(primaryPlots), "#696969", this.polylineEvents));
        }

        if (secondaryPlots.length > 0) {
            this.polylines.push(this.mapService.createPolyline(this.getPolylineId(), this.mapService.convertPlotsToGoogleLatLng(secondaryPlots), "#27ae60", this.polylineEvents));
        }
    }

    private setHighlightedPlot(plot: IPlot) {
        if (!plot) {
            return;
        }

        plot.veriMarkerOptions = this.veriMarkerService.createVeriMarkerOptions(plot, true);
        this.googleMarkerService.generateMarkerOptions(plot, plot.veriMarkerOptions).then(options => {

            plot.markerModel.options = options;

            this.highlightedPlot = plot;
            this.highlightPloyline();
        });
    }

    private createRouteMarkerOptions(routeStop: IRouteStop, index: number, max: number): IGoogleMapsMarkerOptions {

        let icon = "---";

        let markerType = "marker-event-pause";
        let label = routeStop.displayLabel;

        if (index === 0) {
            icon = "play_arrow";
            label = `Start: ${label}`;
            markerType = "marker-event-start";
        }
        else if (index === max - 1) {
            icon = "stop"; //flag
            label = `End: ${label}`;
            markerType = "marker-event-end";
        }

        const options = this.googleMarkerService.createEventMarkerOptions({ label: label, markerClass: markerType, subIcon: icon, showLabel: true });

        // Bit hacky
        if (icon === "---") {
            options.labelContent = options.labelContent.replace(`<i class='material-icons marker-sub-icon'>${icon}</i>`, `<span class='marker-sub-icon text'>${index}</span>`);
        }

        options.visible = true;
        options.draggable = true;

        return options;
    }
}

export class VeriMapTMSAuditComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;
    require: { [index: string]: string; };

    constructor() {
        this.bindings = {
            routeStops: "<",
            routeReplay: "<",
            plots: "<",
            fit: "<",
            selectedAudit: "<",

            onAuditSelected: "&"
        };

        this.require = {
            veriMap: "^veriMap"
        };

        this.controller = ComponentController;
        this.template = require("./veriMapTMSAudit.html");
    }
}
